
import React from 'react';
import type { Language, MealPlan, WeekStartDay } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import { DAYS_OF_WEEK } from '../types';

interface WeekChecklistProps {
    weeks: Date[][];
    selectedWeeks: Set<number>;
    onToggleWeek: (idx: number) => void;
    mealPlans: Record<string, MealPlan>;
    language?: Language;
    weekStartDay?: WeekStartDay;
    compact?: boolean;
}

const formatDateLocal = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const WeekChecklist: React.FC<WeekChecklistProps> = ({
    weeks,
    selectedWeeks,
    onToggleWeek,
    mealPlans,
    language,
    weekStartDay = 'Monday',
    compact = false
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    const getWeekLabel = (startDate: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const day = today.getDay();
        let diff;
        if (weekStartDay === 'Sunday') diff = today.getDate() - day;
        else {
            const dayIndex = day === 0 ? 6 : day - 1;
            diff = today.getDate() - dayIndex;
        }
        const currentWeekStart = new Date(today);
        currentWeekStart.setDate(diff);

        const nextWeekStart = new Date(currentWeekStart);
        nextWeekStart.setDate(currentWeekStart.getDate() + 7);

        if (startDate.toDateString() === currentWeekStart.toDateString()) {
            return { primary: t('weekThis'), isHighlight: true };
        }
        if (startDate.toDateString() === nextWeekStart.toDateString()) {
            return { primary: t('weekNext'), isHighlight: false };
        }

        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        const label = `${startDate.toLocaleDateString(language, options)}`;
        return { primary: label, isHighlight: false };
    };

    const getMealCount = (weekDates: Date[]) => {
        const weekKey = formatDateLocal(weekDates[0]);
        const plan = mealPlans[weekKey];
        if (!plan) return 0;

        return DAYS_OF_WEEK.reduce((acc, day) => {
            return acc + (plan[day]?.length || 0);
        }, 0);
    };

    return (
        <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2'} w-full animate-fade-in`}>
            {weeks.map((week, idx) => {
                const isSelected = selectedWeeks.has(idx);
                const { primary, isHighlight } = getWeekLabel(week[0]);
                const mealCount = getMealCount(week);
                const dateRange = `${week[0].getDate()}/${week[0].getMonth() + 1} - ${week[6].getDate()}/${week[6].getMonth() + 1}`;

                return (
                    <button
                        key={idx}
                        onClick={() => onToggleWeek(idx)}
                        className={`
                            w-full rounded-2xl border transition-all flex items-center justify-between group
                            ${compact ? 'p-2.5' : 'p-4'}
                            ${isSelected 
                                ? 'bg-brand-sage/5 dark:bg-brand-sage/20 border-brand-sage ring-1 ring-brand-sage shadow-sm' 
                                : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}
                        `}
                    >
                        <div className="flex flex-col text-start min-w-0 flex-grow">
                            <span className={`font-black uppercase tracking-tight ${compact ? 'text-xs' : 'text-sm'} ${isSelected ? 'text-brand-sage dark:text-brand-sage' : (isHighlight ? 'text-brand-orange' : 'text-slate-800 dark:text-slate-200')}`}>
                                {primary}
                            </span>
                            <span className={`${compact ? 'text-[8px]' : 'text-[10px]'} font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5`}>
                                {dateRange}
                            </span>
                        </div>

                        <div className={`${compact ? 'mx-2' : 'mx-4'} flex-shrink-0`}>
                            <div className={`rounded-full font-black flex items-center gap-1 shadow-sm transition-colors ${compact ? 'px-1.5 py-0.5 text-[8px]' : 'px-2.5 py-1 text-[10px]'} ${isSelected ? 'bg-brand-sage text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                                <span>🍽️</span>
                                <span>{mealCount}</span>
                            </div>
                        </div>

                        <div className={`${compact ? 'w-4 h-4 border' : 'w-6 h-6 border-2'} rounded-full flex items-center justify-center transition-all flex-shrink-0 ${isSelected ? 'bg-brand-sage border-brand-sage text-white' : 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600'}`}>
                            {isSelected && (
                                <svg className={compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default WeekChecklist;
